import { Plane, Ship, Truck, Package, ShieldCheck, ClipboardCheck } from 'lucide-react';
import { ContentData } from './types';

export const CONTENT: Record<'en' | 'ar', ContentData> = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About Us',
      contact: 'Contact',
      portal: 'Client Portal',
    },
    hero: {
      title: 'Logistics, Elevated.',
      subtitle: 'Orchestrating global movement with precision. Sea, Air, and Land solutions tailored for the discerning enterprise.',
      placeholder: 'Search services...',
      trackBtn: 'Explore',
    },
    about: {
      title: 'Who We Are',
      subtitle: 'Green Line Company for Customs Clearance',
      description: 'We are honored to introduce ourselves as the Green Line Company for Customs Clearance. With a qualified and professional staff, we have over ten years of experience working in Libya in the fields of customs clearance and logistics. We work hard to offer a high level of professionalism, and have been honored to have had some of the largest companies - both Libyan and international - and agencies of the Libyan government as our clients.',
      stats: {
        experience: '10+',
        experienceLabel: 'Years of Experience',
        clients: '220+',
        clientsLabel: 'Clients Served',
      }
    },
    services: {
      title: 'Service Collection',
      subtitle: 'A curated suite of logistical capabilities.',
      items: [
        {
          id: 'sea-freight',
          title: 'Ocean Freight',
          description: 'Strategic container solutions connecting Benghazi to major global hubs.',
          icon: Ship,
          details: 'Our Ocean Freight services stand as the backbone of global trade connectivity for Libya. We offer a comprehensive range of solutions, from Full Container Load (FCL) to Less than Container Load (LCL), ensuring that businesses of all sizes can move their cargo efficiently. \n\nLeveraging long-standing partnerships with major shipping lines, we guarantee competitive rates and priority handling at Benghazi and other major Libyan ports. Our team manages every aspect of the maritime journey, from consolidation and documentation to port handling and final delivery.',
          features: ['Full Container Load (FCL)', 'Less than Container Load (LCL)', 'Project Cargo Handling', 'Port-to-Port & Door-to-Door', 'Marine Insurance Coverage'],
          image: 'https://images.unsplash.com/photo-1577705998148-6da4f3963bc8?q=80&w=2574&auto=format&fit=crop'
        },
        {
          id: 'air-freight',
          title: 'Air Cargo',
          description: 'Expedited transit for time-critical assets and high-value merchandise.',
          icon: Plane,
          details: 'When time is the critical factor, our Air Cargo solutions provide the speed and reliability required for high-value and time-sensitive shipments. We understand that air freight is a premium service, and we treat it with the urgency it demands.\n\nWhether it is spare parts for industrial machinery, pharmaceuticals requiring temperature control, or urgent commercial samples, our air freight division ensures rapid transit through Benina International Airport and other key hubs. We handle all airport formalities to ensure smooth customs release immediately upon arrival.',
          features: ['Express Priority Shipping', 'Charter Services', 'Perishable & Cold Chain', 'Airport-to-Airport', 'Customs Expediting'],
          image: 'https://images.unsplash.com/photo-1520697968532-6e27926715f3?q=80&w=2574&auto=format&fit=crop'
        },
        {
          id: 'customs',
          title: 'Customs Brokerage',
          description: 'Navigating regulatory complexities with seasoned precision.',
          icon: ClipboardCheck,
          details: 'Customs clearance is our heritage and core expertise. Navigating Libyan customs regulations requires deep local knowledge, established relationships, and precise attention to detail. Green Line Company minimizes risk and delay by ensuring full compliance before your cargo even arrives.\n\nOur certified brokers handle complex tariff classifications, duty calculations, and all necessary permitting for restricted goods. We act as your strategic advisor, helping you understand the regulatory landscape to optimize your supply chain costs and timelines.',
          features: ['Import & Export Clearance', 'Tariff Classification', 'Duty Drawback & Exemptions', 'Regulatory Compliance Consulting', 'Documentation Management'],
          image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2670&auto=format&fit=crop'
        },
        {
          id: 'road-transport',
          title: 'Inland Transport',
          description: 'Secure, GPS-monitored ground fleets across North Africa.',
          icon: Truck,
          details: 'The journey doesn’t end at the port. Our Inland Transport network connects Libya’s ports to its hinterlands and beyond. We operate a fleet of modern, GPS-monitored vehicles capable of handling everything from standard containers to heavy machinery.\n\nSafety and security are paramount on Libyan roads. We implement rigorous route planning and real-time monitoring to ensure your cargo reaches its final destination intact and on schedule. Our drivers are trained professionals experienced in navigating local road conditions.',
          features: ['GPS-Tracked Fleet', 'Container Haulage', 'Heavy Equipment Transport', 'Cross-border Logistics', 'Dedicated Distribution'],
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2670&auto=format&fit=crop'
        },
        {
          id: 'lashing',
          title: 'Safety Certification',
          description: 'Certified lashing and cargo security assessments.',
          icon: ShieldCheck,
          details: 'Cargo safety is not just a preference; it is a requirement. Green Line offers specialized Lashing and Securing services to ensure that your cargo withstands the rigors of transport. We provide internationally recognized certificates often required by banks (LCs) and insurance companies.\n\nOur team inspects and secures vehicles, machinery, and break-bulk cargo within containers or on flat racks. We issue official reports documenting the securing method, providing peace of mind and financial compliance for international transactions.',
          features: ['Certified Lashing Reports', 'Vehicle Securing', 'Break-bulk Inspection', 'Dangerous Goods Safety', 'Load Planning'],
          image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2670&auto=format&fit=crop'
        },
        {
          id: 'warehousing',
          title: 'Distribution',
          description: 'State-of-the-art storage facilities with inventory management.',
          icon: Package,
          details: 'Our warehousing solutions offer more than just storage; they provide a strategic buffer in your supply chain. Located near major transit points, our facilities allow for efficient staging, consolidation, and distribution of goods.\n\nWe offer both bonded and non-bonded storage options, giving you flexibility in managing cash flow and duty payments. With modern inventory management systems, you maintain visibility of your stock levels, ensuring that your goods are ready for market exactly when needed.',
          features: ['Short & Long-term Storage', 'Bonded Warehousing', 'Inventory Management', 'Pick and Pack Services', 'Container Stuffing/De-stuffing'],
          image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?q=80&w=2535&auto=format&fit=crop'
        },
      ]
    },
    clients: {
      title: 'Clients & Partners',
      subtitle: 'Trust & Collaboration',
      description: 'We serve as the strategic logistical partner for leading enterprises across energy, manufacturing, and retail. By forging powerful alliances with global shipping lines and air carriers, we ensure your cargo navigates the complexities of international trade with speed, security, and precision.'
    },
    process: {
      title: 'Operational Flow',
      steps: [
        { title: 'Consultation', desc: 'Strategic assessment of needs.' },
        { title: 'Clearance', desc: 'Documentation & Compliance.' },
        { title: 'Transit', desc: 'Multi-modal execution.' },
        { title: 'Delivery', desc: 'Final mile precision.' },
      ]
    },
    team: {
      title: 'Our Leadership',
      subtitle: 'The experts behind our precision.',
      members: [
        {
          name: 'Mr. Abdulkader Alatrash',
          role: 'Transportation Official',
          phone: '+218 916762952',
          image: '/Abulkader_Atrash.png',
        },
        {
          name: 'Mr. Barakat Kebkab',
          role: 'Financial Manager',
          email: 'barakat.kebkab@greenline-ly.com',
          phone: '+218 913765126',
          image: '/Barakat_kebkab.png',
        },
        {
          name: 'Mr. Ahmed Alsheikh',
          role: 'Operation Manager',
          email: 'ahmad.alsheikh@greenline-ly.com',
          phone: '+218 914492751',
          image: '/Ahmed_Alsheikh.png',
        },
        {
          name: 'Mr. Mohamed Elsamin',
          role: 'Tabalino Branch Manager',
          email: 'mohamed.alsameen@greenline-ly.com',
          phone: '+218 926748284',
          image: '/Mohamed_Elsamin.png',
        },
        {
          name: 'Mr. Read Alsharef',
          role: 'Public Relations Manager',
          email: 'motaz.elsamin@greenline-ly.com',
          phone: '+218 917738859',
          image: '/Read_Alsharif.png',
        },
      ]
    },
    contact: {
      title: 'Connect',
      subtitle: 'Initiate a partnership.',
      address: 'Al-Madina Tower, 3rd Floor, Office no: 5, Siles St, Benghazi - Libya',
      phone: '+218 92-8376938',
      email: 'Info@greenline-ly.com',
      form: {
        name: 'Name / Company',
        email: 'Business Email',
        phone: 'Contact Number',
        message: 'Logistical Requirements',
        submit: 'Request Consultation'
      }
    },
    footer: {
      desc: 'Redefining the art of movement since 2013.',
      links: 'Navigation',
      legal: 'GreenLine Logistics © 2025.'
    }
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      services: 'الخدمات',
      about: 'من نحن',
      contact: 'اتصل بنا',
      portal: 'بوابة العملاء',
    },
    hero: {
      title: 'فن الحركة واللوجستيات.',
      subtitle: 'ندير حركة التجارة العالمية بدقة متناهية. حلول بحرية، جوية، وبرية مصممة للمؤسسات الرائدة.',
      placeholder: 'بحث في الخدمات...',
      trackBtn: 'استكشف',
    },
    about: {
      title: 'من نحن',
      subtitle: 'شركة الخط الأخضر للتخليص الجمركي',
      description: 'نتشرف بتقديم أنفسنا كشركة الخط الأخضر للتخليص الجمركي. مع كادر مؤهل ومحترف، لدينا أكثر من عشر سنوات من الخبرة في العمل في ليبيا في مجالات التخليص الجمركي والخدمات اللوجستية. نعمل بجد لتقديم مستوى عالٍ من الاحترافية، وتشرفنا بوجود بعض أكبر الشركات - الليبية والدولية - والهيئات الحكومية الليبية كعملاء لنا.',
      stats: {
        experience: '+١٠',
        experienceLabel: 'سنوات خبرة',
        clients: '+٢٢٠',
        clientsLabel: 'عميل',
      }
    },
    services: {
      title: 'مجموعة الخدمات',
      subtitle: 'باقة مختارة من القدرات اللوجستية.',
      items: [
        {
          id: 'sea-freight',
          title: 'الشحن البحري',
          description: 'حلول حاويات استراتيجية تربط بنغازي بالمراكز العالمية الرئيسية.',
          icon: Ship,
          details: 'تعتبر خدمات الشحن البحري لدينا العمود الفقري لربط التجارة في ليبيا. نقدم مجموعة شاملة من الحلول، بدءًا من حمولات الحاويات الكاملة (FCL) إلى الحمولات الجزئية (LCL)، مما يضمن أن الشركات من جميع الأحجام يمكنها نقل بضائعها بكفاءة.\n\nمن خلال الاستفادة من شراكات طويلة الأمد مع خطوط الشحن الرئيسية، نضمن أسعارًا تنافسية وأولوية في المناولة في ميناء بنغازي والموانئ الليبية الرئيسية الأخرى. يدير فريقنا كل جانب من جوانب الرحلة البحرية، من التجميع والوثائق إلى مناولة الموانئ والتسليم النهائي.',
          features: ['حمولات حاويات كاملة (FCL)', 'حمولات جزئية (LCL)', 'مناولة بضائع المشاريع', 'من الميناء إلى الميناء ومن الباب إلى الباب', 'تغطية التأمين البحري'],
          image: 'https://images.unsplash.com/photo-1577705998148-6da4f3963bc8?q=80&w=2574&auto=format&fit=crop'
        },
        {
          id: 'air-freight',
          title: 'الشحن الجوي',
          description: 'نقل سريع للأصول الحساسة للوقت والبضائع عالية القيمة.',
          icon: Plane,
          details: 'عندما يكون الوقت هو العامل الحاسم، توفر حلول الشحن الجوي لدينا السرعة والموثوقية المطلوبة للشحنات عالية القيمة والحساسة للوقت. نحن ندرك أن الشحن الجوي خدمة متميزة، ونتعامل معها بالسرعة التي تتطلبها.\n\nسواء كانت قطع غيار للآلات الصناعية، أو أدوية تتطلب التحكم في درجة الحرارة، أو عينات تجارية عاجلة، يضمن قسم الشحن الجوي لدينا عبورًا سريعًا عبر مطار بنينا الدولي والمراكز الرئيسية الأخرى. نتولى جميع الإجراءات الرسمية في المطار لضمان الإفراج الجمركي السلس فور الوصول.',
          features: ['شحن سريع ذو أولوية', 'خدمات تأجير الطائرات (Charter)', 'سلسلة التبريد والبضائع القابلة للتلف', 'من المطار إلى المطار', 'تسريع الإجراءات الجمركية'],
          image: 'https://images.unsplash.com/photo-1520697968532-6e27926715f3?q=80&w=2574&auto=format&fit=crop'
        },
        {
          id: 'customs',
          title: 'التخليص الجمركي',
          description: 'التنقل عبر التعقيدات التنظيمية بدقة وخبرة.',
          icon: ClipboardCheck,
          details: 'التخليص الجمركي هو جوهر خبرتنا وتراثنا. يتطلب التنقل في اللوائح الجمركية الليبية معرفة محلية عميقة، وعلاقات راسخة، واهتمامًا دقيقًا بالتفاصيل. تقلل شركة الخط الأخضر من المخاطر والتأخير من خلال ضمان الامتثال الكامل قبل وصول بضائعك حتى.\n\nيتولى وسطاؤنا المعتمدون تصنيفات التعريفة المعقدة، وحسابات الرسوم، وجميع التصاريح اللازمة للبضائع المقيدة. نعمل كمستشارك الاستراتيجي، ونساعدك على فهم المشهد التنظيمي لتحسين تكاليف سلسلة التوريد والجداول الزمنية.',
          features: ['تخليص الواردات والصادرات', 'تصنيف التعريفة الجمركية', 'استرداد الرسوم والإعفاءات', 'استشارات الامتثال التنظيمي', 'إدارة الوثائق'],
          image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2670&auto=format&fit=crop'
        },
        {
          id: 'road-transport',
          title: 'النقل البري',
          description: 'أساطيل برية آمنة ومراقبة بنظام GPS عبر شمال إفريقيا.',
          icon: Truck,
          details: 'لا تنتهي الرحلة عند الميناء. تربط شبكة النقل الداخلي لدينا موانئ ليبيا بمناطقها الداخلية وما وراءها. ندير أسطولًا من المركبات الحديثة المراقبة بنظام GPS والقادرة على التعامل مع كل شيء بدءًا من الحاويات القياسية إلى الآلات الثقيلة.\n\nالسلامة والأمن أمران بالغان الأهمية على الطرق الليبية. نحن نطبق تخطيطًا صارمًا للطرق ومراقبة في الوقت الفعلي لضمان وصول بضائعك إلى وجهتها النهائية سليمة وفي الموعد المحدد. سائقونا محترفون مدربون وذوو خبرة في التنقل في ظروف الطرق المحلية.',
          features: ['أسطول مراقب بنظام GPS', 'نقل الحاويات', 'نقل المعدات الثقيلة', 'لوجستيات عبر الحدود', 'توزيع مخصص'],
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2670&auto=format&fit=crop'
        },
        {
          id: 'lashing',
          title: 'شهادات السلامة',
          description: 'تقييمات معتمدة لتربيط وتأمين البضائع.',
          icon: ShieldCheck,
          details: 'سلامة البضائع ليست مجرد تفضيل؛ بل هي متطلب أساسي. تقدم الخط الأخضر خدمات متخصصة في التربيط والتأمين (Lashing) لضمان تحمل بضائعك لظروف النقل القاسية. نقدم شهادات معترف بها دوليًا تطلبها غالبًا البنوك (الاعتمادات المستندية) وشركات التأمين.\n\nيقوم فريقنا بفحص وتأمين المركبات والآلات والبضائع السائبة داخل الحاويات أو على الحوامل المسطحة. نصدر تقارير رسمية توثق طريقة التأمين، مما يوفر راحة البال والامتثال المالي للمعاملات الدولية.',
          features: ['تقارير تربيط معتمدة', 'تأمين المركبات', 'فحص البضائع السائبة', 'سلامة البضائع الخطرة', 'تخطيط التحميل'],
          image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2670&auto=format&fit=crop'
        },
        {
          id: 'warehousing',
          title: 'التخزين والتوزيع',
          description: 'مرافق تخزين حديثة مع إدارة المخزون.',
          icon: Package,
          details: 'تقدم حلول التخزين لدينا أكثر من مجرد مساحة؛ فهي توفر حاجزًا استراتيجيًا في سلسلة التوريد الخاصة بك. تقع مرافقنا بالقرب من نقاط العبور الرئيسية، مما يسمح بالتجهيز والتجميع والتوزيع الفعال للبضائع.\n\nنقدم خيارات تخزين جمركية وغير جمركية، مما يمنحك المرونة في إدارة التدفق النقدي ودفع الرسوم. مع أنظمة إدارة المخزون الحديثة، تحافظ على رؤية مستويات مخزونك، مما يضمن أن بضائعك جاهزة للسوق بالضبط عند الحاجة.',
          features: ['تخزين قصير وطويل الأجل', 'تخزين جمركي', 'إدارة المخزون', 'خدمات الفرز والتغليف', 'تعبئة وتفريغ الحاويات'],
          image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?q=80&w=2535&auto=format&fit=crop'
        },
      ]
    },
    clients: {
      title: 'العملاء والشركاء',
      subtitle: 'ثقة وتعاون',
      description: 'نعمل كشريك لوجستي استراتيجي للمؤسسات الرائدة في مجالات الطاقة والتصنيع والتجزئة. من خلال إقامة تحالفات قوية مع خطوط الشحن العالمية وشركات النقل الجوي، نضمن أن تبحر بضائعك عبر تعقيدات التجارة الدولية بسرعة وأمان ودقة.'
    },
    process: {
      title: 'سير العمليات',
      steps: [
        { title: 'الاستشارة', desc: 'التقييم الاستراتيجي للاحتياجات.' },
        { title: 'التخليص', desc: 'الوثائق والامتثال.' },
        { title: 'العبور', desc: 'تنفيذ متعدد الوسائط.' },
        { title: 'التسليم', desc: 'دقة الميل الأخير.' },
      ]
    },
    team: {
      title: 'قيادتنا',
      subtitle: 'الخبراء وراء دقة عملياتنا.',
      members: [
        {
          name: 'السيد عبدالقادر الأطرش',
          role: 'مسؤول النقل',
          phone: '+218 916762952',
          image: '/Abulkader_Atrash.png',
        },
        {
          name: 'السيد بركات كبكب',
          role: 'المدير المالي',
          email: 'barakat.kebkab@greenline-ly.com',
          phone: '+218 913765126',
          image: '/Barakat_kebkab.png',
        },
        {
          name: 'السيد أحمد الشيخ',
          role: 'مدير العمليات',
          email: 'ahmad.alsheikh@greenline-ly.com',
          phone: '+218 914492751',
          image: '/Ahmed_Alsheikh.png',
        },
        {
          name: 'السيد محمد الثمين',
          role: 'مدير فرع طابلينو',
          email: 'mohamed.alsameen@greenline-ly.com',
          phone: '+218 926748284',
          image: '/Mohamed_Elsamin.png',
        },
        {
          name: 'السيد رائد الشريف',
          role: 'مدير العلاقات العامة',
          email: 'motaz.elsamin@greenline-ly.com',
          phone: '+218 917738859',
          image: '/Read_Alsharif.png',
        },
      ]
    },
    contact: {
      title: 'تواصل معنا',
      subtitle: 'ابدأ شراكة عمل.',
      address: 'برج المدينة، الدور الثالث، مكتب رقم ٥، شارع سيليس، بنغازي - ليبيا',
      phone: '+218 92-8376938',
      email: 'Info@greenline-ly.com',
      form: {
        name: 'الاسم / الشركة',
        email: 'البريد الإلكتروني للعمل',
        phone: 'رقم الاتصال',
        message: 'المتطلبات اللوجستية',
        submit: 'طلب استشارة'
      }
    },
    footer: {
      desc: 'إعادة تعريف فن الحركة منذ عام 2013.',
      links: 'روابط',
      legal: 'الخط الأخضر للخدمات اللوجستية © ٢٠٢٥.'
    }
  },
};